import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { FC, HTMLAttributes, ReactNode, useState } from 'react';

const DEFAULT_OPEN_DELAY_MS = 300;
const DEFAULT_SIDE_OFFSET = 8;
const PORTAL_ROOT_ID = 'fabric-registration-v2';

type TooltipProps = {
  /** Trigger element the tooltip is anchored to. */
  children: ReactNode;
  /** Tooltip body content. */
  content: ReactNode;
  /** Optional leading icon, shown in a coloured block before the content. */
  icon?: ReactNode;
  /** Preferred side; flips automatically on collision. */
  side?: Placement;
  /** Gap between the trigger and the tooltip. */
  sideOffset?: number;
  /** Hover open delay (ms). */
  openDelay?: number;
} & Omit<HTMLAttributes<HTMLDivElement>, 'content'>;

const Tooltip: FC<TooltipProps> = ({
  children,
  content,
  icon,
  side = 'top',
  sideOffset = DEFAULT_SIDE_OFFSET,
  openDelay = DEFAULT_OPEN_DELAY_MS,
  className,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open:                 isOpen,
    onOpenChange:         setIsOpen,
    placement:            side,
    strategy:             'fixed',
    middleware:           [offset(sideOffset), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { mouseOnly: true, delay: { open: openDelay } });
  const focus = useFocus(context);
  const tap = useClick(context, { ignoreMouse: true });
  const dismiss = useDismiss(context, { ancestorScroll: true });
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    tap,
    dismiss,
    role,
  ]);

  const root = document.getElementById(PORTAL_ROOT_ID);

  return (
    <>
      <div ref={refs.setReference} className={className} {...getReferenceProps(rest)}>
        {children}
      </div>
      {isOpen && (
        <FloatingPortal root={root}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-modal-2"
            {...getFloatingProps()}
          >
            <div
              className={classNames(
                'flex items-stretch w-fit max-w-xs overflow-clip',
                'rounded-xs shadow-2 text-xs font-bold',
              )}
            >
              {icon && (
                <div className="flex items-center justify-center p-3xs bg-purple-0 text-purple-60">
                  {icon}
                </div>
              )}
              <div className="flex items-center py-3xs px-2xs bg-white text-purple-60" >
                {content}
              </div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default Tooltip;
